import { motion } from "framer-motion";
import { Briefcase, Coins, HeartHandshake, Quote } from "lucide-react";

const inView = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const TAPE = [
  "Work · Money · Relationships",
  "Jo Dikhta Hai, Wo Bikta Hai",
  "Keep Roaring. Keep Growing.",
  "Your Next Door CEO",
  "Highs, Lows & Honest Numbers",
];

const HostSpotlight = () => {
  return (
    <section id="host" className="relative py-16 sm:py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div {...inView(0)} className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-amber-400">The Host</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            A mentor who's done the jobs,
            <br className="hidden sm:block" />
            <span className="text-slate-400">not just read about them.</span>
          </h2>
        </motion.div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6">
          <motion.div {...inView(0.1)} data-testid="host-philosophy-card" className="lg:col-span-3 clay-card p-6 sm:p-10 neu-lift">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-slate-500">The Philosophy</p>
            <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Three Pillars of a Life Well-Leveraged</h3>
            <div className="mt-7 sm:mt-8 space-y-5 sm:space-y-6">
              {[
                { icon: Briefcase, title: "Work", desc: "Building brands and people together — craft, career and the courage to start." },
                { icon: Coins, title: "Money", desc: "The architecture of income — how it's earned, kept, multiplied and respected." },
                { icon: HeartHandshake, title: "Relationships", desc: "The quiet leverage — networks, family, and the people who pick you up." },
              ].map((p) => (
                <div key={p.title} className="flex gap-4 sm:gap-5 items-start">
                  <span className="clay-badge shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-amber-400" />
                  </span>
                  <div>
                    <p className="font-display font-semibold text-slate-100">{p.title}</p>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...inView(0.2)} className="lg:col-span-2 glass-panel rounded-[1.75rem] overflow-hidden neu-lift">
            <div className="relative h-44 sm:h-52 overflow-hidden">
              <img
                src="/images/host-desk.jpg"
                alt="Ankush Mehrotra at his writing desk"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121824] via-transparent to-transparent" />
            </div>
            <div className="p-6 sm:p-10 pt-6">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-slate-500">The Pedigree</p>
            <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Your Next Door CEO</h3>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">
              Investor, author, speaker, philanthropist and salesman — Ankush has lived the
              highs and lows he asks his guests about. From campus seminars to corporate
              boardrooms, his promise is simple: no judgement, just fuel for your decisions.
            </p>
            <div className="mt-7 sm:mt-8 grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { v: "45K+", l: "Investor Circle" },
                { v: "100+", l: "Mentees Guided" },
                { v: "4", l: "Ecosystem Roles" },
                { v: "∞", l: "Candour / Episode" },
              ].map((s) => (
                <div key={s.l} data-testid="host-credentials-stat" className="clay-badge rounded-2xl px-3 sm:px-4 py-4">
                  <p className="font-display text-xl font-extrabold text-gold-gradient">{s.v}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
            </div>
          </motion.div>
        </div>

        <motion.div {...inView(0.25)} className="mt-8 sm:mt-10 glass-panel rounded-full overflow-hidden py-3 sm:py-4">
          <div className="quote-tape flex whitespace-nowrap gap-8 sm:gap-12 w-max">
            {[...TAPE, ...TAPE].map((q, i) => (
              <span key={i} className="flex items-center gap-3 text-xs sm:text-sm font-serif-quote tracking-[0.08em] sm:tracking-[0.12em] uppercase text-slate-400">
                <Quote className="w-3.5 h-3.5 text-amber-500/70" /> {q}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HostSpotlight;
